import { useMemo } from 'react';

import { NodeStorageMetrics, Workload } from '@cast/types';

import { NodeWithDependencies } from 'features/cluster/nodes/types';

import { useNodeStorageQuery } from 'hooks/queries/nodes';
import { NodeListFilters } from './_components/NodeListFilters';

import { NodesTable } from './_components/NodesTable';
import { Summary } from './_components/Summary';
import {
  kentNodeListCriteria,
  nodeListCriteria,
  nodeListId,
} from './constants';
import { prepareNode } from './utils';
import {
  useCluster,
  isKentEligibleCluster,
} from '@cast/shared-cluster-context-data-access';

import { SearchProvider } from '@cast/shared-search-ui';

import { Stack } from '@cast/design-system';

type Props = {
  isLoading?: boolean;
  nodes?: NodeWithDependencies[];
  error?: unknown;
  refetch?: () => void;
  workloads?: Workload[];
};

export const NodeList = (props: Props) => {
  const { nodes = [], isLoading, error, refetch, workloads } = props;
  const { cluster, isCastAnywhere } = useCluster();

  const { data: storageData } = useNodeStorageQuery({
    clusterId: cluster.id,
  });

  const storageByNode = useMemo(() => {
    const map = new Map<string, NodeStorageMetrics>();
    storageData?.nodes?.forEach((node) => {
      if (node.nodeName) {
        map.set(node.nodeName, node);
      }
    });
    return map;
  }, [storageData]);

  const hasStorageData = Boolean(storageData?.nodes?.length);

  const criteria = isKentEligibleCluster(cluster)
    ? kentNodeListCriteria
    : nodeListCriteria;

  const preparedNodes = useMemo(() => {
    return nodes?.map((node) =>
      prepareNode(node, storageByNode.get(node.name), isCastAnywhere)
    );
  }, [nodes, storageByNode, isCastAnywhere]);

  return (
    <SearchProvider
      isLoading={isLoading}
      data={preparedNodes}
      criteria={criteria}
      urlKey={nodeListId}
    >
      <Stack gap={24} flex={1}>
        {isLoading ? <Summary.Skeleton /> : <Summary nodes={preparedNodes} />}
        <Stack flex={1} data-hc="success">
          <NodeListFilters workloads={workloads} />
          <NodesTable
            error={error}
            refetch={refetch}
            hasStorageData={hasStorageData}
          />
        </Stack>
      </Stack>
    </SearchProvider>
  );
};
